// THE HOME TABS' START SCREEN — Chat, Present (functions/homeTabStart.js;
// Nika 2026-10-23: "instead of [fly,dashboard,agent] rename [Main, Dashboard,
// Chat, Present, Build] and draw related experience for each tab aligning AI
// mode"). The Fly start screen's shape (components/AssistantFlyStart.js), the
// tab's own asks:
//   the head   the day and the workspace · one heading — whom you
//              ask, then what the tab makes · one line on how it works
//   the asks   tiles in the tab's mode — the Fly start tile; a press asks it
//              through the composer's own send (homeTabStartAsk)
//   the kept   what the person already has of that kind — Present's decks;
//              a press opens it. A part with nothing leaves no gap.
// Build's tab is the app builder itself (components/HomeBuildView.js), not a
// start screen. The mount names its tab (`tab: 'present' | 'chat'` —
// pages/main.js mounts Present's in the home's grid area, the conversation
// view mounts Chat's as its empty state). It holds no logic: every value comes from
// functions/homeTabStart.js through `el.call`. Shown only while its tab has
// nothing on screen yet; it never moves a tile it already shows.
export const HomeTabStart = {
  tag: 'section',
  tab: 'chat',
  dataHomeTabStart: true,
  dataTab: (el) => el.tab,
  dataOpen: (el, s) => (el.call('homeTabStartShown', s.root, el.tab) ? 'true' : 'false'),
  ariaLabel: (el, s) => {
    const copy = el.call('homeTabStartCopy', s.root, el.tab) || {}
    return [copy.lead, copy.rest].filter(Boolean).join(' ') || null
  },
  hide: (el, s) => !el.call('homeTabStartShown', s.root, el.tab),
  flow: 'y',
  gap: 'D',
  width: '100%',
  maxWidth: 'var(--home-grid-w, 100%)',
  marginInline: 'auto',
  minWidth: '0',
  boxSizing: 'border-box',
  animation: 'fadeInUpShort',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',
  // FA204 / frank: the one helper the parts share travels with the screen.
  scope: {
    // The mount's tab, read off the screen element (the parts sit under it).
    homeTab(el) {
      let node = el
      for (let i = 0; node && i < 12; i++) {
        if (node.dataHomeTabStart === true && typeof node.tab === 'string') return node.tab
        node = node.parent
      }
      return 'chat'
    }
  },

  // ── THE HEAD ──────────────────────────────────────────────────────────────
  TabStartHead: {
    tag: 'header',
    flow: 'y',
    gap: 'Y',
    minWidth: '0',

    // The day and the workspace, in the page eyebrow's voice (Fly's own).
    TabStartEyebrow: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z',
      fontWeight: '500',
      lineHeight: '1.3',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'inactive',
      text: (el, s) => {
        const day = el.call('localizedDate', Date.now(), {
          weekday: 'long',
          day: 'numeric',
          month: 'long'
        })
        const scope = String(el.call('assistantScopeName', s.root) || '')
        return scope ? day + ' · ' + scope : String(day || '')
      }
    },

    // One text node keeps the word boundary in both the DOM and spoken name.
    TabStartTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'B',
      fontWeight: '700',
      lineHeight: '1.3',
      color: 'title',
      overflowWrap: 'anywhere',
      text: (el, s) => {
        const copy = el.call('homeTabStartCopy', s.root, el.scope.homeTab(el)) || {}
        return [copy.lead, copy.rest].filter(Boolean).join(' ')
      }
    },

    // Chat's line is the conversation's own empty hint, under the tiles.
    TabStartLead: {
      tag: 'p',
      margin: '0',
      maxWidth: 'H',
      fontSize: 'Z2',
      lineHeight: '1.5',
      color: 'caption',
      hide: (el, s) => !(el.call('homeTabStartCopy', s.root, el.scope.homeTab(el)) || {}).line,
      text: (el, s) => (el.call('homeTabStartCopy', s.root, el.scope.homeTab(el)) || {}).line || ''
    }
  },

  // ── THE ASKS — three tracks (two on a phone), every row as tall as the
  // tallest, so the tiles line up whatever their words ──────────────────────
  TabStartTiles: {
    role: 'group',
    dataHomeTabTiles: true,
    ariaLabel: (el, s) =>
      (el.call('homeTabStartCopy', s.root, el.scope.homeTab(el)) || {}).tiles || null,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gridAutoRows: 'minmax(var(--spacing-E), 1fr)',
    gap: 'Z',
    minWidth: '0',
    '@mobileL': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
    hide: (el, s) => !(el.call('homeTabStartTiles', s.root, el.scope.homeTab(el)) || []).length,
    children: (el, s) => el.call('homeTabStartTiles', s.root, el.scope.homeTab(el)),
    childrenAs: 'state',
    childExtends: 'HomeTabStartTile'
  },

  // ── WHAT THE PERSON ALREADY HAS of the tab's kind ──────────────────────────
  TabStartKept: {
    tag: 'section',
    dataHomeTabKept: true,
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    maxWidth: 'I',
    ariaLabel: (el, s) =>
      (el.call('homeTabStartCopy', s.root, el.scope.homeTab(el)) || {}).kept || null,
    hide: (el, s) => !(el.call('homeTabStartItems', s.root, el.scope.homeTab(el)) || []).length,
    AssistantFlyStartPartTitle: {
      text: (el, s) => (el.call('homeTabStartCopy', s.root, el.scope.homeTab(el)) || {}).kept || ''
    },
    TabStartKeptList: {
      role: 'list',
      flow: 'y',
      gap: 'X',
      minWidth: '0',
      children: (el, s) => el.call('homeTabStartItems', s.root, el.scope.homeTab(el)),
      childrenAs: 'state',
      childExtends: 'HomeTabStartItem'
    }
  }
}

// AN ASK TILE — the Fly start tile, asking in the tab's mode. Its card is the
// home's glass on the home, the solid card over a page (the press's two
// grounds, read for this screen — homeTabStartCard).
export const HomeTabStartTile = {
  extends: 'AssistantFlyStartTile',
  background: (el) => el.call('homeTabStartCard', el),
  onClick: (e, el, s) => el.call('homeTabStartAsk', s)
}

// ONE KEPT ITEM — a deck or an app, in the Recent row's shape: its name, what it
// is when that says something (a deck not saved yet), the arrow; a press opens
// it (homeTabStartOpen).
export const HomeTabStartItem = {
  extends: 'AssistantFlyStartRecentPage',
  background: (el) => el.call('homeTabStartCard', el),
  ariaLabel: (el, s) => (s && s.title) || null,
  title: null,
  onClick: (e, el, s) => el.call('homeTabStartOpen', s),
  RecentWhen: {
    text: (el, s) => {
      if (s && s.meta) return s.meta
      return s && s.at ? String(el.call('timeAgo', s.at) || '') : ''
    }
  }
}
